/**
 * 구글 전환 연동 설정 — 데모 영상 1·2·6·7 장면이 전부 이 화면에서 난다.
 *
 * 구글 OAuth 검증은 *"각 sensitive scope 가 켜는 기능"* 을 영상으로 보이라고 요구한다.
 * 우리 sensitive scope 는 `datamanager` 하나뿐이고, 그 scope 가 켜는 기능은
 * **전환을 고객 광고 계정으로 보내는 것**이다. 그래서 이 화면에는 연동 버튼만이
 * 아니라 **전송 실행과 그 결과**가 함께 있어야 한다 —
 * 가장 흔한 반려 사유가 "동의 화면까지만 찍고 기능을 안 보여주는 것" 이다.
 *
 * ⚠️ 테스트 전송의 기본은 **검증 전용(validateOnly)** 이다. 실전송은 사용자가
 * 명시적으로 켜야 한다 — 광고 계정에 들어간 데이터는 되돌릴 수 없다.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { AlertTriangle, ExternalLink, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { HiddenFeatureNotFound } from '@/components/HiddenFeatureNotFound'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select, SelectOption } from '@/components/ui/Select'
import { Toggle } from '@/components/ui/Toggle'
import { GoogleEventLogsSection } from '@/features/google/GoogleEventLogsSection'
import { apiMessage } from '@/lib/apiError'
import { SetupPage } from '@/features/setup/SetupPage'
import { useConfirm } from '@/hooks/useConfirm'
import { auth } from '@/lib/auth'
import { useShop } from '@/lib/shopContext'
import { getGetShopQueryKey } from '@/api/generated/v2-shop/v2-shop'
import {
  getGetGoogleEventLogsQueryKey,
  getGetGoogleStatusQueryKey,
  googleOAuthAuthorize,
  useDisconnectGoogle,
  useGetGoogleStatus,
  useListGoogleAdAccounts,
  useListGoogleConversionActions,
  useSaveGoogleSettings,
  useSendGoogleTestConversion,
} from '@/api/generated/v2-google/v2-google'

export const Route = createFileRoute('/_dashboard/setup/google')({
  component: GoogleSetupPage,
})

/**
 * 전환 액션을 등록할 이벤트 목록.
 *
 * 구글은 이벤트 이름이 아니라 **전환 액션 ID 로** 전환 종류를 가른다. 그래서 샵마다
 * 이벤트별 ID 를 따로 들어야 한다. 지금은 구매 하나만 연다 —
 * 나머지 7종은 트래커 레그(견적 C단계)가 붙어야 실제로 발화한다.
 */
const CONVERSION_EVENTS: { key: string; label: string }[] = [
  { key: 'Purchase', label: '구매 완료' },
]

/**
 * ⭐ 히든 게이트 — 실험실 플래그(`shops.lab_features_enabled`, 어드민 토글)가 꺼진 샵이면 **첫 줄에서** 잘라낸다.
 *    제목·설명·연동 버튼을 한 줄도 렌더하지 않고, 아래 본문의 쿼리도 안 나간다.
 *    전에는 사이드바 링크만 없을 뿐 주소를 치면 누구나 연동까지 됐다(2026-09-26).
 * ⚠️ 이 플래그는 노출용이다 — 실제 차단은 `/v2/google/*` 404(`v2/google/gate.py`)가 한다.
 */
function GoogleSetupPage() {
  const { shop } = useShop()
  if (!shop?.google_enabled) return <HiddenFeatureNotFound />
  return <GoogleSetupContent />
}

function GoogleSetupContent() {
  const navigate = useNavigate()
  // ⭐ 1차 버전은 성과 수집만이다(결정 A, 2026-09-28) — 전환 매핑·테스트 전송·전송 기록은
  //    전송 스위치(`GOOGLE_SENDING_ENABLED_SHOP_IDS`) 샵에만 보인다. 예외는 OAuth 심사자 샵.
  //    ⚠️ 노출용이다 — 매핑 저장·테스트 전송·구매 웹훅 전송은 서버가 같은 판정으로 막는다.
  const { shop } = useShop()
  const sendingEnabled = !!shop?.google?.sending_enabled
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialog } = useConfirm()

  useEffect(() => {
    if (!auth.isAuthenticated()) navigate({ to: '/' })
  }, [navigate])

  const { data: status, isLoading, error: statusError } = useGetGoogleStatus({
    query: {
      enabled: auth.isAuthenticated(),
      // 404(허용 목록 밖)는 재시도하지 않는다 — 일시적 5xx·네트워크 오류는 그대로 재시도한다
      retry: (count, err) => (err as { status?: number } | null)?.status !== 404 && count < 3,
    },
  })
  // 플래그가 늦게 반영된 경우(샵 응답 캐시 등)에도 API 가 404 면 같은 화면으로 떨어뜨린다
  const statusNotFound =
    (statusError as { status?: number } | null)?.status === 404 ||
    (statusError as { response?: { status?: number } } | null)?.response?.status === 404

  const [accountId, setAccountId] = useState('')
  const [conversionActions, setConversionActions] = useState<
    Record<string, string>
  >({})
  const [formInitialized, setFormInitialized] = useState(false)

  // 서버 값으로 폼을 1회 초기화한다. 매 렌더 동기화하면 입력 중에 값이 되돌아간다.
  useEffect(() => {
    if (!status || formInitialized) return
    setAccountId(status.account_id ?? '')
    setConversionActions(status.conversion_actions ?? {})
    setFormInitialized(true)
  }, [status, formInitialized])

  // 팝업이 아니라 같은 탭으로 돌아온 경우의 복귀 신호 처리.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('google_return') !== '1') return
    if (params.get('google_ok') === '1') {
      toast.success('구글 계정이 연동되었어요')
    } else {
      toast.error(callbackErrorMessage(params.get('google_err')))
    }
    queryClient.invalidateQueries({ queryKey: getGetGoogleStatusQueryKey() })
    // 사이드바 완료 표시·홈 체크리스트는 샵 응답(`shop.google`)을 본다 — 함께 갱신한다
    queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
    // 새로고침해도 토스트가 반복되지 않게 쿼리스트링을 지운다
    window.history.replaceState({}, '', window.location.pathname)
  }, [queryClient])

  // ⚠️ 서버에선 GET 이지만 **버튼을 눌렀을 때만** 불러야 해서 생성 query 훅이 아니라
  //    생성 **함수**를 mutationFn 으로 쓴다. 이유는 둘이다:
  //    ① state 에 TTL 이 있다(`STATE_TTL_MINUTES = 30`). 마운트 시점에 만들어 두면
  //       사용자가 늦게 누를 때 만료돼 콜백이 `invalid_state` 로 떨어진다
  //    ② 연동 버튼을 누르지 않는 대다수에게 매번 불필요한 호출이 나간다
  //    (state 가 한 번 쓰면 소모된다는 서술이 한때 있었으나 **사실이 아니다** —
  //     서명 JWT 이고 서버에 저장·소진되지 않는다)
  const connectMutation = useMutation({
    mutationFn: () => googleOAuthAuthorize(),
    onSuccess: ({ authorize_url }) => {
      // 팝업이 막히면 같은 탭으로 진행한다 — 콜백이 두 경우 모두를 처리한다.
      const popup = window.open(authorize_url, 'google-oauth', 'width=520,height=680')
      if (!popup) {
        window.location.href = authorize_url
        return
      }
      // ⚠️ **팝업이 닫혀도 부모는 아무 신호를 못 받는다.**
      //    콜백 HTML 은 `window.close()` 를 하고, 실패했을 때만 같은 탭으로
      //    리다이렉트한다(`google_return=1`). 즉 **팝업이 정상적으로 닫히는 흔한
      //    경우에 갱신 경로가 아예 없어서**, 사용자는 동의를 마치고도 '미연동'
      //    화면을 계속 본다 (2026-09-09 실측 — DB 에는 connected 로 저장돼 있었다).
      //
      //    `postMessage` 를 쓰지 않는 이유는 콜백 주석과 같다 — COOP 가 내려가면
      //    `window.opener` 가 끊긴다. 반면 **우리가 연 창의 `closed`** 는 그 영향을
      //    받지 않으므로, 닫힘을 감시해 그때 상태를 다시 읽는다.
      const timer = window.setInterval(() => {
        if (!popup.closed) return
        window.clearInterval(timer)
        queryClient.invalidateQueries({ queryKey: getGetGoogleStatusQueryKey() })
        // 사이드바 완료 표시·홈 체크리스트는 샵 응답(`shop.google`)을 본다 — 함께 갱신한다
        queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
      }, 500)
      // 사용자가 팝업을 열어둔 채 잊어버려도 타이머가 영원히 남지 않게 한다.
      // state TTL(30분)보다 짧을 이유가 없어 같은 값으로 맞춘다.
      window.setTimeout(() => window.clearInterval(timer), 30 * 60 * 1000)
    },
    onError: () => toast.error('구글 연동을 시작하지 못했어요'),
  })

  const saveMutation = useSaveGoogleSettings({
    mutation: {
      onSuccess: () => {
        toast.success('저장되었어요')
        queryClient.invalidateQueries({ queryKey: getGetGoogleStatusQueryKey() })
        // 사이드바 완료 표시·홈 체크리스트는 샵 응답(`shop.google`)을 본다 — 함께 갱신한다
        queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
      },
      onError: (e: unknown) => toast.error(apiMessage(e, '저장하지 못했어요')),
    },
  })

  const disconnectMutation = useDisconnectGoogle({
    mutation: {
      onSuccess: (result) => {
        setFormInitialized(false)
        queryClient.invalidateQueries({ queryKey: getGetGoogleStatusQueryKey() })
        // 사이드바 완료 표시·홈 체크리스트는 샵 응답(`shop.google`)을 본다 — 함께 갱신한다
        queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
        toast.success(
          result.revoked_at_google
            ? '구글 연동이 해제되었어요'
            : '연동을 해제했어요 (구글 쪽 권한 회수는 확인되지 않았어요)',
        )
      },
      onError: () => toast.error('연동을 해제하지 못했어요'),
    },
  })

  const connected = !!status?.connected
  // `status` 가 undefined 인 것은 **로딩 중**이고, 그때 false 는 맞다.
  // 필드 자체는 optional 이 아니다 — `schemas.py` 에서 기본값을 떼어
  // OpenAPI required 로 만들었다. 서버가 빠뜨리면 프론트가 조용히 "정상" 으로
  // 읽는 대신 **백엔드에서 pydantic 이 터진다.**
  const credentialInvalid = !!status?.credential_invalid
  // 연동은 됐는데 필수 권한 일부가 없다(이 검사 이전 연동이거나 나중에 권한이 줄었다).
  // 새 연동은 서버가 애초에 저장하지 않는다(`scope_missing`).
  const missingScopes = status?.connected ? (status.missing_scopes ?? []) : []

  // 안내도 스위치를 따른다 — 1차 샵에 "전환을 보낼 수 있어요" 라고 말하면 없는 기능을 약속한다
  const guide = sendingEnabled ? (
    <div className="space-y-4 text-sm text-on-surface-variant">
      <p>
        구글 계정을 연동하면 쇼핑몰에서 일어난 전환을 구글 광고 계정으로 직접
        보낼 수 있어요. 브라우저 태그가 놓치는 전환(이탈 결제·간편결제 미복귀)까지
        서버에서 채웁니다.
      </p>
      <ol className="list-decimal pl-5 space-y-2">
        <li>&lsquo;구글 계정 연동&rsquo; 버튼을 눌러 광고 계정 권한을 허용해 주세요.</li>
        <li>
          목록에서 광고 계정과 전환 액션을 골라 저장해 주세요.
          <span className="block text-xs mt-1">
            목록이 비어 있으면 다시 연동하면서 모든 권한을 허용해 주세요.
          </span>
        </li>
        <li>
          &lsquo;테스트 전송&rsquo; 으로 형식이 맞는지 먼저 확인한 뒤 실제 전송을
          켜 주세요.
        </li>
      </ol>
    </div>
  ) : (
    <div className="space-y-4 text-sm text-on-surface-variant">
      <p>
        구글 계정을 연동하면 구글 광고의 일별 광고비·성과를 매일 자동으로 불러와요.
        전환 데이터를 구글로 보내거나 광고를 수정하지 않아요.
      </p>
      <ol className="list-decimal pl-5 space-y-2">
        <li>
          &lsquo;구글 계정 연동&rsquo; 버튼을 눌러 권한 화면의 모든 항목을 체크해 주세요.
        </li>
        <li>
          목록에서 성과를 가져올 광고 계정을 골라 저장해 주세요.
          <span className="block text-xs mt-1">
            목록이 비어 있으면 다시 연동하면서 모든 권한을 허용해 주세요.
          </span>
        </li>
      </ol>
    </div>
  )

  if (statusNotFound) return <HiddenFeatureNotFound />

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-on-surface-variant" />
      </div>
    )
  }

  return (
    <SetupPage
      title={sendingEnabled ? '구글 전환 연동' : '구글 광고 연동'}
      connected={connected}
      warningBadge={credentialInvalid ? '재연동 필요' : undefined}
      externalLink="https://ads.google.com/"
      externalLinkLabel="구글 광고 열기"
      guide={guide}
      // --- 7 장면: 연동 해제 — 공통 틀 ⑤(맨 아래 회색 텍스트). 규칙은 agent_docs/connect_ux.md §2 ---
      disconnect={
        connected
          ? {
              description: sendingEnabled
                ? '연동을 해제하면 전환 전송이 멈추고, 구글 계정에서도 권한을 회수해요.'
                : '연동을 해제하면 광고 성과 수집이 멈추고, 구글 계정에서도 권한을 회수해요.',
              loading: disconnectMutation.isPending,
              onClick: async () => {
                const ok = await confirm(
                  sendingEnabled
                    ? '전환 전송이 중단되고, 구글 계정에서도 이 앱의 권한이 회수돼요.'
                    : '광고 성과 수집이 중단되고, 구글 계정에서도 이 앱의 권한이 회수돼요.',
                  {
                    title: '구글 연동을 해제할까요?',
                    confirmText: '연동 해제',
                    variant: 'danger',
                  },
                )
                if (ok) disconnectMutation.mutate()
              },
            }
          : undefined
      }
    >
      <div className="space-y-6">
        {status?.configured === false && (
          <div className="flex gap-2 p-4 rounded-lg bg-yellow-50 text-yellow-900 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>
              구글 연동이 서버에 아직 설정되지 않았어요. 잠시 후 다시 시도하거나
              담당자에게 문의해 주세요.
            </p>
          </div>
        )}

        {/* 필수 권한 누락 — 고객이 고칠 수 있는 상태라 무엇을 할지 바로 말한다 */}
        {!credentialInvalid && missingScopes.length > 0 && (
          <div className="flex gap-2 p-4 rounded-lg bg-amber-50 text-amber-900 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">구글 권한 일부가 빠져 있어요.</p>
              <p className="mt-1">
                {missingScopes.map((sc) => scopeLabel(sc, sendingEnabled)).join(' · ')} 권한이 없어요. '구글 계정 다시 연동' 버튼으로 다시 연동하면서
                구글 권한 화면의 모든 항목을 체크해 주세요.
              </p>
            </div>
          </div>
        )}

        {/* ⚠️ 연동됨과 자격증명 유효성은 다른 축이다 — 둘 다 보여준다 */}
        {credentialInvalid && (
          <div className="flex gap-2 p-4 rounded-lg bg-red-50 text-red-900 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">구글 연동이 만료되었어요.</p>
              <p className="mt-1">
                {sendingEnabled
                  ? '전환이 더 이상 전송되지 않아요.'
                  : '광고 성과를 더 이상 가져오지 못해요.'}{' '}
                '구글 계정 다시 연동' 버튼으로 다시 연동해 주세요.
                {status?.invalid_reason && (
                  <span className="block mt-1">{invalidReasonMessage(status.invalid_reason)}</span>
                )}
                {status?.invalid_at && (
                  <span className="block text-xs mt-1">
                    {new Date(status.invalid_at).toLocaleString('ko-KR')} 부터
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* --- 1·2 장면: 진입점과 연동 버튼 --- */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-on-surface">구글 계정</h2>
            {connected ? (
              <Badge variant={credentialInvalid ? 'warning' : 'success'}>
                {credentialInvalid ? '재연동 필요' : '연동됨'}
              </Badge>
            ) : (
              <Badge variant="secondary">미연동</Badge>
            )}
          </div>

          {connected && status?.connected_at && (
            <p className="text-sm text-on-surface-variant">
              {new Date(status.connected_at).toLocaleString('ko-KR')} 에 연동했어요.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              // 정상 연동 상태의 '다시 연동' 은 주 행동이 아니다 — 만료·권한 누락 때만 강조한다
              variant={connected && !credentialInvalid && missingScopes.length === 0 ? 'outline' : 'primary'}
              onClick={() => connectMutation.mutate()}
              loading={connectMutation.isPending}
              disabled={status?.configured === false}
              className="min-h-[44px] sm:min-h-0"
            >
              <ExternalLink className="h-4 w-4 mr-1.5" />
              {connected ? '구글 계정 다시 연동' : '구글 계정 연동'}
            </Button>
          </div>
        </section>

        {connected && (
          <>
            {/* --- 광고 계정·전환 액션 (API 조회 드롭다운) --- */}
            <AccountAndActionsSection
              accountId={accountId}
              onAccountIdChange={setAccountId}
              conversionActions={conversionActions}
              onConversionActionsChange={setConversionActions}
              canUseAdsApi={!!status?.can_use_ads_api}
              adsApiConfigured={!!status?.ads_api_configured}
              hasMissingScopes={missingScopes.length > 0}
              showConversionMapping={sendingEnabled}
              savedAccountId={status?.account_id ?? null}
              savedLoginAccountId={status?.login_account_id ?? null}
              saving={saveMutation.isPending}
              // ⚠️ 이름과 MCC ID 는 **고른 계정에서** 온다. 예전엔 이름을
              //    `status.account_name`(= 저장돼 있던 옛 값)으로 보냈는데, 그러면
              //    계정을 바꿔도 이름이 이전 계정 것으로 남는다.
              onSave={(account) =>
                saveMutation.mutate({
                  data: {
                    account_id: accountId,
                    account_name: account.name ?? null,
                    // 전송이 닫힌 샵은 매핑을 보내지 않는다(서버가 값 있는 매핑을 거절한다).
                    // 빈 객체는 병합이라 기존 매핑을 지우지 않는다.
                    conversion_actions: sendingEnabled ? conversionActions : {},
                    login_account_id: account.loginAccountId,
                  },
                })
              }
              onReconnect={() => connectMutation.mutate()}
            />

            {/* --- ⭐ 6 장면: scope 가 켜는 기능 — 전송 스위치 샵(심사자)에만 --- */}
            {sendingEnabled && (
              <TestConversionSection
                hasAccount={!!status?.account_id}
                hasAction={!!status?.conversion_actions?.Purchase}
              />
            )}
          </>
        )}

        {/* --- 6 장면의 '결과 화면' --- */}
        {/* ⚠️ 연동 여부로 막지 않는다. 기록은 샵의 이력이라 해제한 뒤에도 남고(서버도
            shop_id 로만 거른다), 구글 OAuth 심사자는 **미연동 상태**로 들어와 이 표에서
            datamanager 처리 결과·사유를 확인한다(2026-09-21 — 촬영 뒤 연동을 해제했다).
            기록이 없으면 섹션 안의 빈 상태가 뜬다. */}
        {sendingEnabled && (
          <GoogleEventLogsSection
            limit={10}
            datamanagerMissing={missingScopes.includes('datamanager')}
          />
        )}

        {/* 성과 표는 이 화면에서 뺐다 — 심사자 샵 포함 모든 샵(아론 10-01). 연동 화면과 광고 성과 화면을 나눈다
            (connect_ux.md §1). `GooglePerformanceSection` 과 성과 API 는 나중 성과 페이지로 옮기려고 남겨 둔다.
            ⚠️ 09-23 OAuth 승인 근거 화면이었다 — 재심사·scope 변경 때는 성과 페이지로 다시 보여줘야 한다. */}
      </div>
      <ConfirmDialog />
    </SetupPage>
  )
}

/**
 * 광고 계정과 전환 액션을 **목록에서 고르게** 한다.
 *
 * 수기 입력을 없앤 이유: 전환 액션 ID 를 오입력해도 **에러가 아니라 엉뚱한 전환
 * 액션에 조용히 쌓인다.** 광고 리포트를 보기 전에는 틀렸다는 걸 알 수 없고,
 * 몰당 최대 8개라 오입력 기회가 여덟 번이다.
 *
 * ⚠️ 조회가 막히는 사유가 셋인데 **고칠 사람이 다르다.** 뭉뚱그리면 고객이
 * 헛수고를 한다:
 *   · developer token 미설정 → 우리가 고친다 (고객은 아무것도 할 수 없다)
 *   · `adwords` 미동의       → 고객사가 재연동해야 한다
 *   · 그 외 오류             → 재시도
 */
function AccountAndActionsSection({
  accountId,
  onAccountIdChange,
  conversionActions,
  onConversionActionsChange,
  canUseAdsApi,
  adsApiConfigured,
  hasMissingScopes,
  showConversionMapping,
  savedAccountId,
  savedLoginAccountId,
  saving,
  onSave,
  onReconnect,
}: {
  accountId: string
  onAccountIdChange: (v: string) => void
  conversionActions: Record<string, string>
  onConversionActionsChange: (
    updater: (prev: Record<string, string>) => Record<string, string>,
  ) => void
  canUseAdsApi: boolean
  adsApiConfigured: boolean
  // 필수 권한 누락은 페이지 위 배너 하나가 말한다 — 여기서 같은 말을 또 하면(그리고 예전 문구처럼
  // "전환 전송은 되지만" 이라고 하면) 한 화면에서 서로 다른 말을 한다(리뷰 지적).
  hasMissingScopes: boolean
  // 전환 매핑 드롭다운을 보일지 — 1차 버전은 성과 수집만이라 전송 스위치 샵에만 보인다
  showConversionMapping: boolean
  // 지금 저장돼 있는 계정·MCC. 같은 계정인데 경로만 어긋나면 화면이 그것을 알린다.
  savedAccountId: string | null
  savedLoginAccountId: string | null
  saving: boolean
  // 고른 계정에서 함께 가져가야 하는 값들. 계정과 **세트로** 저장해야 어긋나지 않는다.
  onSave: (account: { name: string | null; loginAccountId: string | null }) => void
  onReconnect: () => void
}) {
  const dropdownUsable = canUseAdsApi && adsApiConfigured

  const {
    data: accounts,
    isLoading: accountsLoading,
    // ⚠️ **조회 실패를 "계정 0개" 로 그리지 마라.** 둘은 고칠 사람이 다르고(우리 vs
    //    고객사), 게다가 실패하면 저장 버튼까지 막히므로 사유를 안 보여주면 화면이
    //    통째로 잠긴 것처럼 보인다. 백엔드가 이미 고객 언어로 사유를 만들어 준다.
    error: accountsError,
    refetch: refetchAccounts,
  } = useListGoogleAdAccounts({
    query: { enabled: dropdownUsable },
  })

  // ⚠️ 선택된 계정이 **MCC 하위**면 그 MCC 의 ID 를 함께 보내야 한다(안 보내면 403).
  //    반대로 **직접 권한 계정이면 보내면 안 된다**(보내면 403). 계정 목록이 계정마다
  //    올바른 값을 주므로 그대로 넘긴다 — 여기서 추측하지 않는다.
  const selectedAccount = (accounts?.items ?? []).find(
    (a) => a.customer_id === accountId,
  )

  const {
    data: actions,
    isLoading: actionsLoading,
    // ⚠️ **조회 실패를 "액션이 0개" 로 그리지 마라.** 아래 플레이스홀더가 원인을
    //    단정하는 자리라, 사유를 안 받아오면 화면이 **틀린 원인**을 말한다
    //    (계정 목록 배너와 같은 이유).
    error: actionsError,
    refetch: refetchActions,
  } = useListGoogleConversionActions(
    {
      customer_id: accountId,
      ...(selectedAccount?.login_customer_id
        ? { login_customer_id: selectedAccount.login_customer_id }
        : {}),
    },
    // 계정을 고르기 전에는 물어볼 대상이 없다.
    // ⚠️ 계정 목록이 아직 안 왔으면 기다린다 — 그 전에 물으면 login_customer_id
    //    없이 나가서 MCC 하위 계정이 403 으로 떨어진다.
    {
      query: {
        // 매핑을 안 보이는 샵은 전환 액션을 물을 이유가 없다
        enabled: showConversionMapping && dropdownUsable && !!accountId && !!selectedAccount,
      },
    },
  )

  // ⚠️ **서버 전송은 오프라인(업로드) 액션에만 꽂힌다** (2026-09-07 실측 —
  //    웹사이트 액션은 `events:ingest` 에서 전부 NOT_FOUND).
  //    그런데 이름·카테고리만 보면 구분이 안 된다. 실제로 둘 다 category 가
  //    `PURCHASE` 라서, 웹사이트 액션을 고르면 **저장은 성공하고 전송만 실패**한다.
  //    화면에는 "구글 API 오류" 정도만 남아 원인을 알 수 없다.
  //    → 고를 수 없는 것은 **보여주지 않는다.** (같은 이유로 MCC 도 계정 목록에서
  //      제외한다 — 서버 `list_ad_accounts`)
  //
  //    ⚠️ C단계(브라우저 레그)가 오면 WEBPAGE 액션도 쓴다. 그때는 **다른 용도의
  //      드롭다운**이 생기는 것이지 이 필터를 푸는 것이 아니다.
  const uploadActions = (actions?.items ?? []).filter(
    (a) => a.type === 'UPLOAD_CLICKS',
  )

  // 계정 목록이 오는 동안에도 "로딩 중" 이어야 한다 — 그 사이 `selectedAccount` 가
  // 없어서 다른 문구가 깜빡였다가 바뀐다.
  const actionsPending =
    actionsLoading || (!!accountId && !selectedAccount && accountsLoading)
  // 로딩이 끝났는데도 저장된 계정을 목록에서 못 찾은 상태.
  // ⚠️ **조회가 실패한 경우는 제외한다** — 그건 "권한이 회수됐다" 가 아니라 "묻지
  //    못했다" 이고, 두 상태에 같은 문구를 쓰면 고객이 엉뚱한 곳을 고치게 된다.
  // ⚠️ **드롭다운을 쓸 수 없는 상태는 제외한다.** `adwords` 미동의 샵은 계정 목록
  //    쿼리 자체가 disabled 라 `selectedAccount` 가 영영 없는데, 그걸 "찾아봤지만
  //    없다" 로 그리면 **묻지도 않고 단정하는** 문구가 된다.
  const accountMissing =
    dropdownUsable &&
    !actionsPending &&
    !accountsError &&
    !!accountId &&
    !selectedAccount
  // 저장된 계정 **그대로인데** 접근 경로만 달라진 상태 — 저장을 눌러야 전송에 반영된다.
  // (연동을 다시 하면서 권한 경로가 바뀐 경우가 이 모양이다)
  //
  // ⚠️ **계정이 같을 때만 본다.** 계정 비교를 빼면 "아직 저장한 적 없는 MCC 고객사"
  //    가 계정을 고르는 순간 항상 뜬다(저장값은 null, 고른 값은 MCC) — 첫 설정
  //    화면 대부분이 경고를 보게 되고, 하필 그 화면이 심사 데모 영상 무대다.
  //    계정을 바꿔 고르는 중인 것도 드리프트가 아니라 그냥 선택이다.
  const loginAccountDrifted =
    !!selectedAccount &&
    selectedAccount.customer_id === savedAccountId &&
    (selectedAccount.login_customer_id ?? null) !== (savedLoginAccountId ?? null)

  return (
    <section className="space-y-4 pt-2 border-t border-outline-variant">
      <div>
        <h2 className="text-base font-semibold text-on-surface">
          {showConversionMapping ? '광고 계정과 전환 액션' : '광고 계정'}
        </h2>
        <p className="text-sm text-on-surface-variant mt-1">
          {showConversionMapping
            ? '구글은 이벤트 이름이 아니라 전환 액션으로 전환 종류를 구분해요. 이벤트마다 전환 액션을 따로 골라 주세요.'
            : '광고 성과를 가져올 구글 광고 계정을 골라 주세요.'}
        </p>
      </div>

      {/* 조회가 막힌 사유를 구분해서 안내한다 — 고칠 사람이 다르다 */}
      {!adsApiConfigured && (
        <div className="flex gap-2 p-4 rounded-lg bg-yellow-50 text-yellow-900 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <p>
            광고 계정 조회가 서버에 아직 설정되지 않았어요. 담당자에게 문의해 주세요.
          </p>
        </div>
      )}
      {adsApiConfigured && !canUseAdsApi && !hasMissingScopes && (
        <div className="flex gap-2 p-4 rounded-lg bg-yellow-50 text-yellow-900 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">
              연동할 때 광고 계정 조회 권한이 허용되지 않았어요.
            </p>
            <p className="mt-1">
              {showConversionMapping ? '계정·전환 액션 목록' : '광고 계정 목록'}을 불러올 수 없어요. 다시
              연동하면서 <strong>모든 권한을 허용</strong>해 주세요.
            </p>
            <button
              type="button"
              onClick={onReconnect}
              className="mt-2 text-sm font-medium underline"
            >
              다시 연동하기
            </button>
          </div>
        </div>
      )}

      <Select
        label="구글 광고 계정"
        value={accountId}
        disabled={!dropdownUsable || accountsLoading}
        onChange={(e) => {
          onAccountIdChange(e.target.value)
          // ⚠️ 계정을 바꾸면 전환 액션도 무효다 — 다른 계정의 액션 ID 를 그대로 두면
          //    저장은 되는데 전송이 조용히 엉뚱한 곳으로 간다.
          onConversionActionsChange(() => ({}))
        }}
        className="min-h-[44px] sm:min-h-0"
      >
        <SelectOption value="">
          {accountsLoading ? '불러오는 중…' : '선택해 주세요'}
        </SelectOption>
        {(accounts?.items ?? []).map((a) => (
          <SelectOption key={a.customer_id} value={a.customer_id}>
            {/* 이름이 있으면 이름을 앞세운다 — 숫자만 보여주면 어느 계정인지
                알 수 없다. 구글이 이름을 안 주는 경우가 있어 ID 로 폴백한다. */}
            {a.name ? `${a.name} (${a.customer_id})` : a.customer_id}
          </SelectOption>
        ))}
      </Select>

      {/* 목록을 못 불러온 것과 계정이 없는 것은 다르다 */}
      {accountsError && (
        <div className="flex gap-2 p-4 rounded-lg bg-red-50 text-red-900 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p>{apiMessage(accountsError, '광고 계정 목록을 불러오지 못했어요')}</p>
            <button
              type="button"
              onClick={() => refetchAccounts()}
              className="mt-2 text-sm font-medium underline"
            >
              다시 시도
            </button>
          </div>
        </div>
      )}

      {/* 어느 권한 경로로 접근하는지 보여준다 — 전송이 403 일 때 원인이 여기인
          경우가 많은데, 값을 감추면 화면만 보고는 "계정은 맞는데 왜 안 되지" 에서
          멈춘다. 저장된 값과 어긋나면 그것도 여기서 드러난다. */}
      {selectedAccount?.login_customer_id && (
        <p className="text-xs text-on-surface-variant -mt-2">
          관리자 계정(MCC) {selectedAccount.login_customer_id} 을 거쳐 접근해요.
        </p>
      )}
      {loginAccountDrifted && (
        <p className="text-xs text-yellow-800 -mt-2">
          접근 경로가 저장된 값과 달라요. <strong>저장</strong>을 눌러야{' '}
          {showConversionMapping ? '전송에' : '성과 조회에'} 반영돼요.
        </p>
      )}

      {showConversionMapping && CONVERSION_EVENTS.map(({ key, label }) => (
        <Select
          key={key}
          label={`${label} 전환 액션`}
          value={conversionActions[key] ?? ''}
          disabled={!dropdownUsable || !accountId || actionsLoading}
          onChange={(e) =>
            onConversionActionsChange((prev) => ({ ...prev, [key]: e.target.value }))
          }
          className="min-h-[44px] sm:min-h-0"
        >
          <SelectOption value="">
            {/* ⚠️ 일곱 상태를 구분한다. 뭉치면 **틀린 원인을 단정하는** 문구가 된다 —
                이 셀렉트는 마지막 갈래가 "없어요" 라 **뭉갠 만큼 전부 그리로 떨어지고**,
                고객사는 멀쩡한 전환 액션을 구글에 새로 만들러 간다:
                · 로딩 중(계정 목록이 아직 안 온 경우 포함)
                · 드롭다운 자체를 못 쓰는 상태(서버 미설정 · `adwords` 미동의).
                  사유는 위 노란 배너가 이미 설명하므로 여기서는 반복하지 않는다
                · **계정 목록 조회가 실패했고 고른 계정을 확인할 수 없음** — 저장된
                  계정이 있으면 `!accountId` 도 `accountMissing`(`!accountsError` 를
                  본다)도 안 걸려 예전엔 "없어요" 로 샜다. 재방문 화면에서 가장 흔한
                  실패 모양이다(401·403·네트워크).
                  ⚠️ `!accountId` **앞**이어야 한다 — 뒤에 두면 저장값이 없을 때
                  "고를 수 없는 목록에서 고르라" 는 막다른 안내가 남는다.
                  ⚠️ **`!selectedAccount` 를 함께 본다.** react-query 는 성공한 뒤의
                  재조회가 실패하면 **직전 `data` 를 유지한 채** `error` 를 세운다 —
                  그때는 목록도 전환 액션도 멀쩡히 떠 있으므로, 고를 수 있는 것을 앞에
                  두고 "확인할 수 없다" 고 말하는 꼴이 된다(정답은 "선택해 주세요")
                · 계정을 아직 안 고름 — **첫 진입 화면이 이 상태다.** 예전엔 여기서도
                  "오프라인 전환 액션이 없어요" 가 떴다(고르지도 않았는데 0개라고 단정)
                · 저장된 계정을 목록에서 못 찾음 — 권한이 바뀌었거나 사용 중지됐다
                · **전환 액션 조회가 실패** — 403(권한 경로)이나 서버 설정 문제.
                  백엔드가 만든 사유는 아래 배너가 띄운다
                · 진짜로 오프라인 액션이 0개 */}
            {actionsPending
              ? '불러오는 중…'
              : !dropdownUsable
                ? '광고 계정 조회를 쓸 수 없어요'
                : accountsError && !selectedAccount
                  ? '광고 계정을 불러오지 못해 확인할 수 없어요'
                  : !accountId
                    ? '광고 계정을 먼저 골라 주세요'
                    : accountMissing
                      ? '선택한 계정을 목록에서 찾지 못했어요'
                      : actionsError
                        ? '전환 액션을 불러오지 못했어요'
                        : uploadActions.length === 0
                          ? '오프라인 전환 액션이 없어요'
                          : '선택해 주세요'}
          </SelectOption>
          {uploadActions.map((a) => (
            <SelectOption key={a.id} value={a.id}>
              {a.name ?? a.id}
            </SelectOption>
          ))}
        </Select>
      ))}

      {/* 사유는 백엔드가 고객 언어로 만들어 준다 — 특히 403 은 "권한이 있는 계정으로
          다시 연동" 과 "관리자 문의" 가 갈리는 자리라(`_ads_error_message`) 여기서
          뭉개면 그 분류가 화면에 도달하지 못한다. */}
      {showConversionMapping && actionsError && (
        <div className="flex gap-2 p-4 rounded-lg bg-red-50 text-red-900 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p>{apiMessage(actionsError, '전환 액션 목록을 불러오지 못했어요')}</p>
            <button
              type="button"
              onClick={() => refetchActions()}
              className="mt-2 text-sm font-medium underline"
            >
              다시 시도
            </button>
          </div>
        </div>
      )}

      {/* ⚠️ **고른 계정을 목록에서 확인하기 전에는 저장할 수 없다.** 목록에 없는
          계정을 그대로 저장하면 `login_account_id` 가 null 로 덮이는데, 그 계정이
          MCC 하위였다면 **저장은 성공하고 전송만 403** 이 된다 — 화면상 정상으로
          보여 가장 나쁜 형태다. 드롭다운을 못 쓰는 상태(권한·설정)에서도 같은
          이유로 막힌다. 사유는 위 경고 배너가 이미 설명한다. */}
      <Button
        onClick={() =>
          onSave({
            name: selectedAccount?.name ?? null,
            loginAccountId: selectedAccount?.login_customer_id ?? null,
          })
        }
        loading={saving}
        disabled={!accountId.trim() || !selectedAccount}
        className="min-h-[44px] sm:min-h-0"
      >
        저장
      </Button>
    </section>
  )
}

/**
 * 전환 1건을 실제로 보내는 자리. 데모 영상에서 **scope 가 무엇을 켜는지** 보여주는
 * 장면이 여기다.
 *
 * ⚠️ 기본은 검증 전용이다. 실전송 토글은 되돌릴 수 없다는 경고와 함께 둔다.
 */
function TestConversionSection({
  hasAccount,
  hasAction,
}: {
  hasAccount: boolean
  hasAction: boolean
}) {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [value, setValue] = useState('30000')
  const [gclid, setGclid] = useState('')
  const [realSend, setRealSend] = useState(false)

  const sendMutation = useSendGoogleTestConversion({
    mutation: {
      onSuccess: (result) => {
        if (result.success) toast.success(result.message)
        else toast.error(result.message)
        // 결과가 바로 아래 로그에 뜨게 한다 — 영상에서 이 연결이 보여야 한다.
        // ⚠️ 생성 쿼리키의 접두어(`/v2/google/event-logs`)로 무효화한다 —
        //    limit·offset 이 키에 섞이므로 접두어 매칭이어야 페이지와 무관하게 듣는다.
        queryClient.invalidateQueries({
          queryKey: getGetGoogleEventLogsQueryKey(),
        })
      },
      onError: (e: unknown) => toast.error(apiMessage(e, '전송하지 못했어요')),
    },
  })

  const blocked = !hasAccount || !hasAction

  return (
    <section className="space-y-4 pt-2 border-t border-outline-variant">
      <div>
        <h2 className="text-base font-semibold text-on-surface">전환 전송 확인</h2>
        <p className="text-sm text-on-surface-variant mt-1">
          전환 1건을 보내 연동이 실제로 동작하는지 확인해요.
        </p>
      </div>

      {blocked && (
        <p className="text-sm text-on-surface-variant">
          광고 계정 ID 와 구매 전환 액션 ID 를 먼저 저장해 주세요.
        </p>
      )}

      <Input
        label="구매자 이메일"
        placeholder="customer@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="min-h-[44px] sm:min-h-0"
      />
      <p className="text-xs text-on-surface-variant -mt-2">
        이메일은 전송 전에 암호화(SHA-256)되며, 원본은 저장하지 않아요.
      </p>

      {/* ⚠️ **이 칸이 없으면 성공을 한 번도 볼 수 없다.** 이메일·전화만 실은 이벤트는
          「리드 확보를 위한 향상된 전환」이 꺼진 계정에서 거절되는데(2026-09-07·09-09
          실측), 그 기능은 Google 태그로만 켤 수 있고 API 옵션이 없다.
          확실히 통하는 식별자는 gclid 다. */}
      <Input
        label="구글 클릭 ID (gclid)"
        placeholder="Cj0KCQjw..."
        value={gclid}
        onChange={(e) => setGclid(e.target.value)}
        className="min-h-[44px] sm:min-h-0"
      />
      <p className="text-xs text-on-surface-variant -mt-2">
        광고를 클릭해 들어온 주소의 <code>gclid</code> 값이에요. 이메일만으로는
        광고 계정에 「향상된 전환」 설정이 없으면 구글이 받지 않아요.
      </p>

      <Input
        label="전환 금액 (원)"
        type="number"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="min-h-[44px] sm:min-h-0"
      />

      <Toggle
        checked={realSend}
        onChange={setRealSend}
        label="실제로 전송하기"
        description="끄면 형식만 검증하고 실제 데이터는 보내지 않아요. 켜면 광고 계정에 전환이 실제로 기록되고 되돌릴 수 없어요."
      />

      <Button
        onClick={() =>
          sendMutation.mutate({
            data: {
              event_name: 'Purchase',
              conversion_value: Number(value) || null,
              currency: 'KRW',
              email: email.trim() || null,
              gclid: gclid.trim() || null,
              validate_only: !realSend,
            },
          })
        }
        loading={sendMutation.isPending}
        // 식별자가 **하나도** 없으면 보낼 이유가 없다(구글이 400 으로 거절한다).
        // ⚠️ 이메일을 필수로 두지 않는다 — gclid 하나만으로 성공하는 것이 오히려
        //    확실한 경로다.
        disabled={blocked || (!email.trim() && !gclid.trim())}
        variant={realSend ? 'primary' : 'secondary'}
        className="min-h-[44px] sm:min-h-0"
      >
        {realSend ? '전환 전송' : '형식 검증'}
      </Button>
    </section>
  )
}

/** 콜백 실패 사유를 고객 언어로. 모르는 코드는 일반 문구로 흘린다. */
/**
 * 서버가 내려주는 필수 권한 이름 → 고객 언어.
 * ⚠️ 1차 샵(전송 스위치 밖)에 datamanager 를 '전환 전송' 이라 부르면 쓰지도 않는 기능을 요구하는
 *    말이 된다 — 중립어로 부른다. 동의 화면은 두 권한을 함께 요청하고(바꾸면 OAuth 재심사) 서버는
 *    둘 다 필수로 본다(결정 A ④ — 부분 동의 연동을 막아 두면 나중에 전송을 켤 때도 안전하다).
 */
function scopeLabel(scope: string, sendingEnabled: boolean): string {
  switch (scope) {
    case 'datamanager':
      return sendingEnabled ? '전환 전송' : '구글 광고 데이터 연결'
    case 'adwords':
      return '광고 계정·성과 조회'
    default:
      return scope
  }
}

/**
 * 자격증명 무효 사유(`invalid_reason`) → 고객 언어. 모르는 사유는 원문 코드를 보여 준다
 * — 숨기면 담당자가 원인을 좇을 단서가 사라진다.
 */
function invalidReasonMessage(reason: string): string {
  switch (reason) {
    case 'invalid_grant':
      return '구글 계정에서 연동 권한이 해제됐거나 만료됐어요.'
    case 'no_refresh_token':
      return '구글에서 연동 정보를 받지 못했어요.'
    default:
      return `사유: ${reason}`
  }
}

function callbackErrorMessage(code: string | null): string {
  switch (code) {
    case 'access_denied':
      return '구글 연동이 취소되었어요'
    case 'no_refresh_token':
      return '연동 권한을 다시 허용해 주세요 (권한 정보가 오지 않았어요)'
    case 'token_exchange_failed':
      return '구글과 통신하지 못했어요. 잠시 후 다시 시도해 주세요'
    case 'scope_missing':
      return '구글 권한 화면에서 체크가 빠진 항목이 있어요. 다시 연동하면서 모든 항목을 체크해 주세요'
    default:
      return '구글 연동에 실패했어요'
  }
}
